import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { streamWiki, type WikiOperation, type WikiStreamEvent } from './wiki-api'
import { useVaultFiles, vaultKeys } from '@/features/vault/use-vault'
import {
  abortRun,
  beginRun,
  clearRun,
  isRunController,
  setRunAbortController,
  updateRunState,
  useRunStore,
} from '@/features/runs/run-store'

export interface WikiTopic {
  slug: string
  name: string
  pageCount: number
  hasAgents: boolean
}

const KNOWLEDGE_PREFIX = '_Knowledge/'

/**
 * Aggregiert die Themenliste aus den Vault-Files (alle Files unter _Knowledge/).
 * Themen sind die direkten Subordner unter _Knowledge/.
 */
export function useWikiTopics(): {
  topics: WikiTopic[]
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => Promise<unknown>
  isFetching: boolean
} {
  const { data, isLoading, isError, error, refetch, isFetching } = useVaultFiles()

  const topics = useMemo(() => {
    const entries = data?.entries ?? []
    const map = new Map<string, WikiTopic>()
    for (const f of entries) {
      if (!f.path.startsWith(KNOWLEDGE_PREFIX)) continue
      const rest = f.path.slice(KNOWLEDGE_PREFIX.length)
      const slashIdx = rest.indexOf('/')
      if (slashIdx === -1) continue
      const slug = rest.slice(0, slashIdx)
      const t = map.get(slug) ?? { slug, name: slug, pageCount: 0, hasAgents: false }
      if (rest.includes('/wiki/') && !rest.endsWith('/.keep') && !rest.includes('/_lint_')) t.pageCount++
      if (rest.endsWith('/AGENTS.md')) t.hasAgents = true
      map.set(slug, t)
    }
    return [...map.values()].sort((a, b) => a.slug.localeCompare(b.slug))
  }, [data])

  return { topics, isLoading, isError, error, refetch, isFetching }
}

/**
 * Liefert alle Vault-Pfade fuer ein Thema (source/, wiki/, AGENTS.md).
 */
export function useWikiTopicTree(slug: string | null): {
  agents?: string
  sources: Array<{ path: string; name: string }>
  pages: Array<{ path: string; name: string; isLintReport: boolean }>
} {
  const { data } = useVaultFiles()
  return useMemo(() => {
    if (!slug) return { sources: [], pages: [] }
    const prefix = `${KNOWLEDGE_PREFIX}${slug}/`
    const entries = (data?.entries ?? []).filter((e) => e.path.startsWith(prefix))
    let agents: string | undefined
    const sources: Array<{ path: string; name: string }> = []
    const pages: Array<{ path: string; name: string; isLintReport: boolean }> = []
    for (const e of entries) {
      if (e.path.endsWith('/.keep')) continue
      if (e.path.endsWith('/AGENTS.md')) agents = e.path
      else if (e.path.includes('/source/')) sources.push({ path: e.path, name: e.name })
      else if (e.path.includes('/wiki/')) {
        const isLint = e.path.includes('/_lint_')
        pages.push({ path: e.path, name: e.name, isLintReport: isLint })
      }
    }
    sources.sort((a, b) => a.name.localeCompare(b.name))
    pages.sort((a, b) => a.name.localeCompare(b.name))
    return { agents, sources, pages }
  }, [data, slug])
}

interface WikiState {
  operation: WikiOperation
  status: string
  detail?: string
  pages: Array<{ path: string; concept: string }>
  answer?: string
  lintReport?: string
  isRunning: boolean
  error?: string
}

export type { WikiState }

export function useWikiInvoke() {
  const qc = useQueryClient()
  // Globaler Store statt lokalem State (M2-B Run-Survival).
  const slice = useRunStore((s) => s.wiki)

  const invoke = useCallback(
    async (options: {
      operation: WikiOperation
      topic?: string
      args?: string
      premium?: boolean
      sessionId?: string
    }) => {
      abortRun('wiki')
      const controller = new AbortController()
      setRunAbortController('wiki', controller)

      beginRun(
        'wiki',
        {
          operation: options.operation,
          status: 'queued',
          pages: [],
          isRunning: true,
        },
        options.sessionId ?? null,
      )

      try {
        await streamWiki({
          ...options,
          signal: controller.signal,
          onEvent: (event: WikiStreamEvent) => {
            if (!isRunController('wiki', controller)) return
            updateRunState('wiki', (prev) => {
              switch (event.type) {
                case 'status':
                  return { ...prev, status: event.phase, detail: event.detail }
                case 'token':
                  return { ...prev, answer: (prev.answer ?? '') + event.content }
                case 'page_written':
                  return {
                    ...prev,
                    pages: [...prev.pages, { path: event.path, concept: event.concept }],
                  }
                case 'done':
                  return {
                    ...prev,
                    status: 'done',
                    isRunning: false,
                    lintReport: event.lint_report_path ?? prev.lintReport,
                    answer: event.query_answer ?? prev.answer,
                  }
                case 'error':
                  return { ...prev, status: 'error', isRunning: false, error: event.message }
                default:
                  return prev
              }
            })
            if (event.type === 'done') {
              void qc.invalidateQueries({ queryKey: vaultKeys.files() })
              if (event.lint_report_path) toast.success(`Lint-Report: ${event.lint_report_path}`)
              else if (event.pages_written?.length) toast.success(`${event.pages_written.length} Page(s) geschrieben.`)
              else if (event.query_answer) toast.success('Memory-Antwort erhalten.')
            }
            if (event.type === 'error') {
              updateRunState('wiki', (prev) => ({ ...prev, status: 'error', isRunning: false, error: event.message }))
              throw new Error(event.message)
            }
          },
        })
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : 'Wiki-Aufruf fehlgeschlagen'
        toast.error(message)
        updateRunState('wiki', (prev) => ({ ...prev, status: 'error', isRunning: false, error: message }))
        throw err
      }
    },
    [qc],
  )

  const reset = useCallback(() => {
    abortRun('wiki')
    clearRun('wiki')
  }, [])

  return { invoke, reset, state: slice.state, sessionId: slice.sessionId }
}
